import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo } from 'react-icons/fa';
import { LuCalendarDays } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import AnnualReportChecklistEditor from './AnnualReportChecklistEditor';

const SLUG = 'annual-report';

const defaultForm = () => ({
  isPublished: true,
  pageTitle: 'Annual Report Service',
  pageSubtitle: '',
  pageIcon: 'LuCalendarDays',
  loadingMessage: '',
  dueDateCardTitle: 'Annual Report Due Date',
  labelState: 'State',
  labelEntityType: 'Entity Type',
  labelFormationDate: 'Formation Date',
  labelFilingYear: 'Filing Year',
  labelDueDate: 'Due Date',
  stepCards: [
    { step: 1, title: 'Initial Information Required', instructions: '' },
    { step: 2, title: 'Annual Report Filed', instructions: 'Administrative review and processing of your annual report.' },
  ],
  stepInstructions: {
    step1Default: '',
    step1Wyoming: '',
    step1NewJersey: '',
    step1DelawareLLC: '',
    step1DelawareCCorp: '',
    step2: '',
  },
  statusBadges: {
    inProgress: 'IN PROGRESS',
    clientPending: 'ACTION REQUIRED',
    completed: 'COMPLETED',
    pending: 'PENDING',
  },
  stepLabels: {
    progress: 'Progress',
    actionRequired: 'Action Required',
    completed: 'Completed',
    workingForEmployee: 'Working for Employee',
  },
  recordsTableDefaultTitle: 'Annual Report Records',
  emptyStateTitle: '',
  emptyStateDescription: '',
  emptyStateButtonLabel: 'Check for Updates',
  errorLabel: 'Error:',
  errorRetryLabel: 'Try Again',
  errorRefreshingLabel: 'Refreshing...',
  toasts: {
    alreadySubmitted: '',
    submitSuccess: '',
    draftSaved: '',
    submitErrorPrefix: 'Error: ',
  },
  checklistShell: {
    backLabel: 'Back',
    saveDraftLabel: 'Save Draft',
    submitLabel: 'Submit Checklist',
  },
  checklists: {},
});

const TABS = [
  { id: 'page', label: 'Page Header' },
  { id: 'dueDate', label: 'Due Date Card' },
  { id: 'steps', label: 'Step Cards' },
  { id: 'instructions', label: 'Step Instructions' },
  { id: 'labels', label: 'Status & Labels' },
  { id: 'empty', label: 'Empty State & Toasts' },
  { id: 'checklists', label: 'Checklist Forms' },
];

const inputClass = 'w-full text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-2 text-gray-900 dark:text-white';
const labelClass = 'block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1';

const Field = ({ label, value, onChange, multiline = false }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {multiline ? (
      <textarea className={inputClass} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const AnnualReportPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('page');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          stepCards: (c.stepCards?.length ? c.stepCards : defaultForm().stepCards).map((card, i) => ({
            ...defaultForm().stepCards[i],
            ...card,
          })),
          stepInstructions: { ...defaultForm().stepInstructions, ...(c.stepInstructions || {}) },
          statusBadges: { ...defaultForm().statusBadges, ...(c.statusBadges || {}) },
          stepLabels: { ...defaultForm().stepLabels, ...(c.stepLabels || {}) },
          toasts: { ...defaultForm().toasts, ...(c.toasts || {}) },
          checklistShell: { ...defaultForm().checklistShell, ...(c.checklistShell || {}) },
          checklists: { ...defaultForm().checklists, ...(c.checklists || {}) },
        });
      }
    } catch {
      toastError('Failed to load Annual Report page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateNested = (section, field, value) =>
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  const updateStepCard = (index, field, value) =>
    setForm((prev) => {
      const stepCards = [...prev.stepCards];
      stepCards[index] = { ...stepCards[index], [field]: value };
      return { ...prev, stepCards };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const normalizedChecklists = Object.fromEntries(
        Object.entries(form.checklists || {}).map(([key, checklist]) => [
          key,
          {
            ...checklist,
            sections: (checklist.sections || []).map((section, sectionIndex) => ({
              ...section,
              order: sectionIndex,
              fields: (section.fields || []).map((field, fieldIndex) => ({
                ...field,
                order: fieldIndex,
              })),
            })),
          },
        ])
      );
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: { ...form, checklists: normalizedChecklists }, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Annual Report page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded Annual Report defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      await fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Annual Report Service Page"
        subtitle="Edit user portal Annual Report page headers, due date labels, step cards, checklist fields, and messages"
        icon={LuCalendarDays}
        onBack={() => navigate('/portal-cms')}
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 space-y-4 mb-6">
        {activeTab === 'page' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Page title" value={form.pageTitle} onChange={(v) => updateField('pageTitle', v)} />
            <Field label="Page icon" value={form.pageIcon} onChange={(v) => updateField('pageIcon', v)} />
            <div className="md:col-span-2">
              <Field label="Page subtitle" value={form.pageSubtitle} onChange={(v) => updateField('pageSubtitle', v)} multiline />
            </div>
            <Field label="Loading message" value={form.loadingMessage} onChange={(v) => updateField('loadingMessage', v)} />
            <Field label="Records table default title" value={form.recordsTableDefaultTitle} onChange={(v) => updateField('recordsTableDefaultTitle', v)} />
          </div>
        )}

        {activeTab === 'dueDate' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Due date card title" value={form.dueDateCardTitle} onChange={(v) => updateField('dueDateCardTitle', v)} />
            </div>
            <Field label="State label" value={form.labelState} onChange={(v) => updateField('labelState', v)} />
            <Field label="Entity type label" value={form.labelEntityType} onChange={(v) => updateField('labelEntityType', v)} />
            <Field label="Formation date label" value={form.labelFormationDate} onChange={(v) => updateField('labelFormationDate', v)} />
            <Field label="Filing year label" value={form.labelFilingYear} onChange={(v) => updateField('labelFilingYear', v)} />
            <Field label="Due date label" value={form.labelDueDate} onChange={(v) => updateField('labelDueDate', v)} />
          </div>
        )}

        {activeTab === 'steps' && (
          <div className="space-y-4">
            {form.stepCards.map((card, index) => (
              <div key={card.step} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">Step {card.step}</h3>
                <Field label="Title" value={card.title} onChange={(v) => updateStepCard(index, 'title', v)} />
                <Field label="Instructions (tooltip)" value={card.instructions} onChange={(v) => updateStepCard(index, 'instructions', v)} multiline />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'instructions' && (
          <div className="grid md:grid-cols-1 gap-4">
            <Field label="Step 1 — default ({state} placeholder)" value={form.stepInstructions.step1Default} onChange={(v) => updateNested('stepInstructions', 'step1Default', v)} multiline />
            <Field label="Step 1 — Wyoming" value={form.stepInstructions.step1Wyoming} onChange={(v) => updateNested('stepInstructions', 'step1Wyoming', v)} multiline />
            <Field label="Step 1 — New Jersey" value={form.stepInstructions.step1NewJersey} onChange={(v) => updateNested('stepInstructions', 'step1NewJersey', v)} multiline />
            <Field label="Step 1 — Delaware LLC" value={form.stepInstructions.step1DelawareLLC} onChange={(v) => updateNested('stepInstructions', 'step1DelawareLLC', v)} multiline />
            <Field label="Step 1 — Delaware C-Corp" value={form.stepInstructions.step1DelawareCCorp} onChange={(v) => updateNested('stepInstructions', 'step1DelawareCCorp', v)} multiline />
            <Field label="Step 2 — default" value={form.stepInstructions.step2} onChange={(v) => updateNested('stepInstructions', 'step2', v)} multiline />
          </div>
        )}

        {activeTab === 'labels' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Badge: In Progress" value={form.statusBadges.inProgress} onChange={(v) => updateNested('statusBadges', 'inProgress', v)} />
            <Field label="Badge: Action Required" value={form.statusBadges.clientPending} onChange={(v) => updateNested('statusBadges', 'clientPending', v)} />
            <Field label="Badge: Completed" value={form.statusBadges.completed} onChange={(v) => updateNested('statusBadges', 'completed', v)} />
            <Field label="Badge: Pending" value={form.statusBadges.pending} onChange={(v) => updateNested('statusBadges', 'pending', v)} />
            <Field label="Progress label" value={form.stepLabels.progress} onChange={(v) => updateNested('stepLabels', 'progress', v)} />
            <Field label="Action required label" value={form.stepLabels.actionRequired} onChange={(v) => updateNested('stepLabels', 'actionRequired', v)} />
            <Field label="Completed label" value={form.stepLabels.completed} onChange={(v) => updateNested('stepLabels', 'completed', v)} />
            <Field label="Working for employee label" value={form.stepLabels.workingForEmployee} onChange={(v) => updateNested('stepLabels', 'workingForEmployee', v)} />
            <Field label="Error label" value={form.errorLabel} onChange={(v) => updateField('errorLabel', v)} />
            <Field label="Error retry button" value={form.errorRetryLabel} onChange={(v) => updateField('errorRetryLabel', v)} />
            <Field label="Error refreshing label" value={form.errorRefreshingLabel} onChange={(v) => updateField('errorRefreshingLabel', v)} />
          </div>
        )}

        {activeTab === 'checklists' && (
          <AnnualReportChecklistEditor form={form} setForm={setForm} />
        )}

        {activeTab === 'empty' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Empty state title" value={form.emptyStateTitle} onChange={(v) => updateField('emptyStateTitle', v)} />
            </div>
            <div className="md:col-span-2">
              <Field label="Empty state description" value={form.emptyStateDescription} onChange={(v) => updateField('emptyStateDescription', v)} multiline />
            </div>
            <Field label="Check for updates button" value={form.emptyStateButtonLabel} onChange={(v) => updateField('emptyStateButtonLabel', v)} />
            <div className="md:col-span-2"><hr className="border-gray-100 dark:border-dark-700" /></div>
            <Field label="Toast: already submitted" value={form.toasts.alreadySubmitted} onChange={(v) => updateNested('toasts', 'alreadySubmitted', v)} multiline />
            <Field label="Toast: submit success" value={form.toasts.submitSuccess} onChange={(v) => updateNested('toasts', 'submitSuccess', v)} multiline />
            <Field label="Toast: draft saved" value={form.toasts.draftSaved} onChange={(v) => updateNested('toasts', 'draftSaved', v)} />
            <Field label="Toast: error prefix" value={form.toasts.submitErrorPrefix} onChange={(v) => updateNested('toasts', 'submitErrorPrefix', v)} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
          <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm font-medium flex items-center gap-2">
          <FaUndo /> {resetting ? 'Resetting...' : 'Reset to Defaults'}
        </button>
        <label className="flex items-center gap-2 text-sm ml-auto cursor-pointer">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>
      </div>
    </PageLayout>
  );
};

export default AnnualReportPortalCMS;
